import React, { useMemo } from 'react';
import {
  type PlaceholderParameter,
  type PlaceholderReference,
} from '../../inputs/rich-text-advanced/menu-bar/placeholder/types.ts';
import { type IReference } from '../../schema/expressions/references/IReference.ts';
import {
  AsyncServicesEngineContextProvider,
  type IAsyncServicesEngineContextProviderProps,
} from '../../schema/expressions/repository/async/context/AsyncServicesEngineContext.tsx';
import { type IRootWidget } from '../../schema/widgets/repository/containers/root/IRootWidget.ts';
import { type Maybe } from '../../utils/typings.ts';
import { TreeReferencesContextProvider } from './TreeReferencesContext.tsx';

export type PlaceholderParameterInputRenderFunction = (
  parameter: PlaceholderParameter,
  onChange?: (value: Maybe<string>) => void,
) => React.ReactNode;

export interface IRootWidgetIdentifier {
  type: string;
  id: string;
  root: IRootWidget;
}

export interface ITreeReferencesGlobalContext {
  /** Tutte le possibili radici (es. sezioni) */
  allRoots: IRootWidgetIdentifier[];
  selectedRootId?: string | null; // La passiamo qui per comodità ma è il posto sbagliato
  /** Altri riferimenti */
  otherReferences: IReference[];
  /** Metadati aggiuntivi disponibili al contesto di validazione */
  metadata?: Record<string, unknown>;

  /** Riferimenti placeholder */
  placeholderReferences?: PlaceholderReference[];
  /** Funzione per renderizzare l'input di un parametro placeholder */
  placeholderParameterInputRenderer?: PlaceholderParameterInputRenderFunction;
}

export const TreeReferencesGloablContext =
  React.createContext<ITreeReferencesGlobalContext | null>(null);

interface ITreeReferencesGlobalContextProviderProps {
  allRoots: IRootWidgetIdentifier[];
  selectedRootId?: string | null; // La passiamo qui per comodità ma è il posto sbagliato
  otherReferences: IReference[];
  children: React.ReactNode;
  /** Metadati aggiuntivi disponibili al contesto di validazione */
  metadata?: Record<string, unknown>;

  /** Elenco dei WebService utilizzabili nelle espressioni */
  async: Omit<IAsyncServicesEngineContextProviderProps, 'children'>;

  /** Riferimenti placeholder */
  placeholderReferences?: PlaceholderReference[];
  /** Funzione per renderizzare l'input di un parametro placeholder */
  placeholderParameterInputRenderer?: PlaceholderParameterInputRenderFunction;
}

/**
 * Definisce il contesto globale di modifica delle espressioni, con informazioni
 * sul campione, le sezioni, ecc.
 */
export function TreeReferencesGlobalContextProvider(
  props: ITreeReferencesGlobalContextProviderProps,
) {
  const {
    allRoots,
    selectedRootId,
    otherReferences,
    metadata,
    async,
    placeholderReferences,
    placeholderParameterInputRenderer,
  } = props;

  const value = useMemo(() => {
    return {
      allRoots,
      selectedRootId,
      otherReferences,
      metadata,
      placeholderReferences,
      placeholderParameterInputRenderer,
    };
  }, [
    allRoots,
    selectedRootId,
    otherReferences,
    metadata,
    placeholderReferences,
    placeholderParameterInputRenderer,
  ]);

  return (
    <TreeReferencesGloablContext.Provider value={value}>
      <AsyncServicesEngineContextProvider {...async}>
        <TreeReferencesContextProvider>
          {props.children}
        </TreeReferencesContextProvider>
      </AsyncServicesEngineContextProvider>
    </TreeReferencesGloablContext.Provider>
  );
}

/**
 * Ritorna il contesto di modifica delle espressioni padre. Il contesto
 * delle espressioni globale non fa riferimento a singoli Widget ma definisce
 * l'insieme delle variabili disponibili a livello root.
 */
export function useTreeReferencesGlobalContext(): ITreeReferencesGlobalContext | null {
  const context = React.useContext(TreeReferencesGloablContext);
  return context;
}
